"use client";

import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  PropsWithChildren,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect
} from "react";
import { SavedHuiswerkSession } from "@/lib/types";
import {
  GroepVraag,
  SamenvattingResponse,
  TheorieGroep
} from "@/services/api_types";
import { WisWizAPIError } from "@/services/api";
import { useAppContext } from "./AppContext";
import { useAuthContext } from "./AuthContext";
import { useContentSelectieContext } from "./ContentSelectieContext";

const MAX_HUISWERK_SAMENVATTING_ATTEMPTS = 3;

export interface HuiswerkItem {
  vraag: GroepVraag;
  subvraagId: number;
  letter: string;
}

type HuiswerkModusContext = {
  huiswerkVraagIds: number[];
  setHuiswerkVraagIds: Dispatch<SetStateAction<number[]>>;
  huiswerkSubvraagIds: number[];
  setHuiswerkSubvraagIds: Dispatch<SetStateAction<number[]>>;
  huiswerkSubvraagIdSet: Set<number>;
  huiswerkGroepen: TheorieGroep[];
  setHuiswerkGroepen: Dispatch<SetStateAction<TheorieGroep[]>>;
  huiswerkItemsByGroep: Map<number, HuiswerkItem[]>;
  isHuiswerkActive: boolean;

  restoreHuiswerkSession: () => void;
  clearHuiswerkSession: (options?: {
    completed?: boolean;
    preserveSession?: boolean;
  }) => void;

  samenvattingen: Map<number, SamenvattingResponse>;
  failedSamenvattingen: Set<number>;
  rateLimitedSamenvattingen: Set<number>;
  loadingSamenvattingen: boolean;
  ensureSamenvatting: (groepId: number) => Promise<void>;

  isHuiswerkSelecting: boolean;
  setIsHuiswerkSelecting: Dispatch<SetStateAction<boolean>>;
  huiswerkSelectedSubvraagIds: Set<number>;
  addToHuiswerkSelection: (subvraagIds: number[]) => void;
  removeFromHuiswerkSelection: (subvraagIds: number[]) => void;
};

const HuiswerkModusContext = createContext<HuiswerkModusContext | null>(null);

export const useHuiswerkModusContext = () => {
  const context = useContext(HuiswerkModusContext);
  if (context === null) {
    throw Error("No HuiswerkModusContext available");
  }
  return context;
};

export const HuiswerkModusContextProvider = ({
  children
}: PropsWithChildren) => {
  const { api, currentModus, setCurrentModus, selectModus } = useAppContext();
  const { userData, setHuiswerkSetOnUserData } = useAuthContext();
  const { currentHoofdstuk } = useContentSelectieContext();

  const [huiswerkVraagIds, setHuiswerkVraagIds] = useState<number[]>([]);
  const [huiswerkSubvraagIds, setHuiswerkSubvraagIds] = useState<number[]>([]);
  const [huiswerkGroepen, setHuiswerkGroepenState] = useState<TheorieGroep[]>(
    []
  );

  // --- Samenvatting state ---
  const [samenvattingen, setSamenvattingen] = useState<
    Map<number, SamenvattingResponse>
  >(new Map());
  const [failedSamenvattingen, setFailedSamenvattingen] = useState<Set<number>>(
    new Set()
  );
  const [rateLimitedSamenvattingen, setRateLimitedSamenvattingen] = useState<
    Set<number>
  >(new Set());
  const [loadingSamenvattingen, setLoadingSamenvattingen] = useState(false);
  const loadedSamenvattingIdsRef = useRef<Set<number>>(new Set());
  const inFlightSamenvattingIdsRef = useRef<Set<number>>(new Set());
  const samenvattingAttemptsRef = useRef<Map<number, number>>(new Map());
  const samenvattingScopeRef = useRef(0);

  const resetSamenvattingState = useCallback(() => {
    samenvattingScopeRef.current += 1;
    loadedSamenvattingIdsRef.current = new Set();
    inFlightSamenvattingIdsRef.current = new Set();
    samenvattingAttemptsRef.current = new Map();
    setSamenvattingen(new Map());
    setFailedSamenvattingen(new Set());
    setRateLimitedSamenvattingen(new Set());
    setLoadingSamenvattingen(false);
  }, []);

  const setHuiswerkGroepen = useCallback<
    Dispatch<SetStateAction<TheorieGroep[]>>
  >(
    (value) => {
      resetSamenvattingState();
      setHuiswerkGroepenState(value);
    },
    [resetSamenvattingState]
  );

  // --- Selection state ---
  const [isHuiswerkSelecting, setIsHuiswerkSelecting] =
    useState<boolean>(false);
  const [huiswerkSelectedSubvraagIds, setHuiswerkSelectedSubvraagIds] =
    useState<Set<number>>(new Set());

  const isHuiswerkActive =
    currentModus === "huiswerk" && huiswerkVraagIds.length > 0;

  const huiswerkSubvraagIdSet = useMemo(
    () => new Set(huiswerkSubvraagIds),
    [huiswerkSubvraagIds]
  );

  const serverSessionApplied = useRef(false);

  const huiswerkItemsByGroep = useMemo(() => {
    const result = new Map<number, HuiswerkItem[]>();
    for (const groep of huiswerkGroepen) {
      const items: HuiswerkItem[] = [];
      for (const v of groep.vragen) {
        for (const s of v.subvragen) {
          if (huiswerkSubvraagIdSet.has(s.id)) {
            items.push({ vraag: v, subvraagId: s.id, letter: s.letter });
          }
        }
      }
      result.set(groep.id, items);
    }
    return result;
  }, [huiswerkGroepen, huiswerkSubvraagIdSet]);

  const addToHuiswerkSelection = useCallback((subvraagIds: number[]) => {
    setHuiswerkSelectedSubvraagIds((prev) => {
      const next = new Set(prev);
      for (const id of subvraagIds) next.add(id);
      return next;
    });
  }, []);

  const removeFromHuiswerkSelection = useCallback((subvraagIds: number[]) => {
    setHuiswerkSelectedSubvraagIds((prev) => {
      const next = new Set(prev);
      for (const id of subvraagIds) next.delete(id);
      return next;
    });
  }, []);

  // --- Functions ---

  const applyServerSession = useCallback((session: SavedHuiswerkSession) => {
    serverSessionApplied.current = true;

    setHuiswerkVraagIds(session.vraagIds);
    setHuiswerkSubvraagIds(session.subvraagIds);
    setHuiswerkGroepen(session.groepen);
  }, []);

  const restoreHuiswerkSession = useCallback(() => {
    // If state already has huiswerk data, just switch mode
    if (huiswerkVraagIds.length > 0) {
      selectModus("huiswerk", "expanded_resume");
      return;
    }
    // Otherwise load from server
    const session = userData?.huiswerk_set;
    if (!session) return;
    selectModus("huiswerk", "expanded_resume");
    applyServerSession(session);
  }, [huiswerkVraagIds, selectModus, userData, applyServerSession]);

  const clearHuiswerkSession = useCallback(
    (options?: { completed?: boolean; preserveSession?: boolean }) => {
      resetSamenvattingState();
      setCurrentModus(undefined);
      setHuiswerkVraagIds([]);
      setHuiswerkSubvraagIds([]);
      setHuiswerkGroepenState([]);
      if (userData && !options?.preserveSession) {
        setHuiswerkSetOnUserData(undefined);
        if (options?.completed) {
          api.completeHuiswerkSet().catch(() => {});
        } else {
          api.deleteHuiswerkSet().catch(() => {});
        }
      }
    },
    [
      setCurrentModus,
      api,
      userData,
      setHuiswerkSetOnUserData,
      resetSamenvattingState
    ]
  );

  const ensureSamenvatting = useCallback(
    async (groepId: number) => {
      if (loadedSamenvattingIdsRef.current.has(groepId)) return;
      if (inFlightSamenvattingIdsRef.current.has(groepId)) return;
      const attempts = samenvattingAttemptsRef.current.get(groepId) ?? 0;
      if (attempts >= MAX_HUISWERK_SAMENVATTING_ATTEMPTS) return;

      inFlightSamenvattingIdsRef.current.add(groepId);
      samenvattingAttemptsRef.current.set(groepId, attempts + 1);
      const scope = samenvattingScopeRef.current;

      setFailedSamenvattingen((prev) => {
        if (!prev.has(groepId)) return prev;
        const next = new Set(prev);
        next.delete(groepId);
        return next;
      });
      setRateLimitedSamenvattingen((prev) => {
        if (!prev.has(groepId)) return prev;
        const next = new Set(prev);
        next.delete(groepId);
        return next;
      });
      setLoadingSamenvattingen(true);

      try {
        const result = await api.hwmSamenvatting(groepId);
        if (scope !== samenvattingScopeRef.current) return;

        loadedSamenvattingIdsRef.current.add(groepId);
        setSamenvattingen((prev) => new Map(prev).set(groepId, result));
      } catch (err) {
        if (scope !== samenvattingScopeRef.current) return;

        if (err instanceof WisWizAPIError && err.statusCode === 429) {
          setRateLimitedSamenvattingen((prev) => new Set(prev).add(groepId));
        } else {
          setFailedSamenvattingen((prev) => new Set(prev).add(groepId));
        }
      } finally {
        if (scope !== samenvattingScopeRef.current) return;

        inFlightSamenvattingIdsRef.current.delete(groepId);
        setLoadingSamenvattingen(inFlightSamenvattingIdsRef.current.size > 0);
      }
    },
    [api]
  );

  // --- Effects ---

  // Restore saved huiswerk session from server on mount
  useEffect(() => {
    const session = userData?.huiswerk_set;
    if (!session) return;
    if (serverSessionApplied.current) return;

    selectModus("huiswerk", "auto_restore");
    applyServerSession(session);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // --- Selection effects ---

  // Auto-enter huiswerk selection when modus is "huiswerk" and no vragen selected yet
  useEffect(() => {
    if (currentModus === "huiswerk" && !isHuiswerkActive && currentHoofdstuk) {
      setIsHuiswerkSelecting(true);
    } else {
      setIsHuiswerkSelecting(false);
    }
  }, [currentModus, isHuiswerkActive, currentHoofdstuk]);

  // Mode boundary: reset huiswerk-selecting when leaving huiswerk mode
  useEffect(() => {
    if (currentModus !== "huiswerk") {
      setIsHuiswerkSelecting(false);
    }
  }, [currentModus]);

  // Clear selection set when exiting huiswerk selecting
  useEffect(() => {
    if (!isHuiswerkSelecting) {
      setHuiswerkSelectedSubvraagIds(new Set());
    }
  }, [isHuiswerkSelecting]);

  const providerValue = useMemo(
    () => ({
      huiswerkVraagIds,
      setHuiswerkVraagIds,
      huiswerkSubvraagIds,
      setHuiswerkSubvraagIds,
      huiswerkSubvraagIdSet,
      huiswerkGroepen,
      setHuiswerkGroepen,
      huiswerkItemsByGroep,
      isHuiswerkActive,
      restoreHuiswerkSession,
      clearHuiswerkSession,
      samenvattingen,
      failedSamenvattingen,
      rateLimitedSamenvattingen,
      loadingSamenvattingen,
      ensureSamenvatting,
      isHuiswerkSelecting,
      setIsHuiswerkSelecting,
      huiswerkSelectedSubvraagIds,
      addToHuiswerkSelection,
      removeFromHuiswerkSelection
    }),
    [
      huiswerkVraagIds,
      huiswerkSubvraagIds,
      huiswerkSubvraagIdSet,
      huiswerkGroepen,
      huiswerkItemsByGroep,
      isHuiswerkActive,
      restoreHuiswerkSession,
      clearHuiswerkSession,
      samenvattingen,
      failedSamenvattingen,
      rateLimitedSamenvattingen,
      loadingSamenvattingen,
      ensureSamenvatting,
      isHuiswerkSelecting,
      huiswerkSelectedSubvraagIds,
      addToHuiswerkSelection,
      removeFromHuiswerkSelection
    ]
  );

  return (
    <HuiswerkModusContext.Provider value={providerValue}>
      {children}
    </HuiswerkModusContext.Provider>
  );
};
